import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const PaymentSuccess = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    refreshUser();
  }, []);

  return (
    <div className="payment-success" data-testid="payment-success">
      <div className="container">
        <motion.div
          className="success-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="success-icon">
            <CheckCircle2 size={64} />
          </div>

          <h1>Payment Successful!</h1>
          <p>Welcome to BlackGirlTrading-Academy {user?.subscription_tier === 'premium' ? 'Premium' : 'Pro'}!</p>

          <div className="tier-badge">
            <Crown size={20} />
            <span>{user?.subscription_tier?.toUpperCase()} Member</span>
          </div>

          <div className="next-steps">
            <h3>What's Next?</h3>
            <div className="steps">
              <Link to="/learn" className="step-item">
                <span className="step-number">1</span>
                <div>
                  <h4>Explore All Tutorials</h4>
                  <p>Access your new unlocked content</p>
                </div>
                <ArrowRight size={18} />
              </Link>
              <Link to="/trade" className="step-item">
                <span className="step-number">2</span>
                <div>
                  <h4>Try Paper Trading</h4>
                  <p>Practice with your increased virtual balance</p>
                </div>
                <ArrowRight size={18} />
              </Link>
              <Link to="/gold-guide" className="step-item">
                <span className="step-number">3</span>
                <div>
                  <h4>Read the Gold Guide</h4>
                  <p>Master gold trading strategies</p>
                </div>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>

          <Link to="/" className="btn btn-primary">
            Go to Dashboard
          </Link>
        </motion.div>
      </div>

      <style>{`
        .payment-success {
          padding: 80px 0;
          min-height: 80vh;
          display: flex;
          align-items: center;
        }

        .success-card {
          max-width: 600px;
          margin: 0 auto;
          text-align: center;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 48px;
        }

        .success-icon {
          color: var(--success);
          margin-bottom: 24px;
        }

        .success-card h1 {
          font-size: 2rem;
          margin-bottom: 8px;
        }

        .success-card > p {
          color: var(--text-secondary);
          margin-bottom: 24px;
        }

        .tier-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: linear-gradient(135deg, var(--primary), var(--secondary));
          padding: 12px 24px;
          border-radius: 50px;
          font-weight: 600;
          margin-bottom: 32px;
        }

        .next-steps {
          text-align: left;
          background: var(--bg-subtle);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 32px;
        }

        .next-steps h3 {
          font-size: 1rem;
          margin-bottom: 16px;
          color: var(--text-muted);
        }

        .steps {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .step-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: var(--bg-paper);
          border-radius: 12px;
          text-decoration: none;
          color: inherit;
          transition: transform 0.2s;
        }

        .step-item:hover {
          transform: translateX(4px);
        }

        .step-number {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--primary);
          border-radius: 50%;
          font-weight: 700;
          font-size: 14px;
        }

        .step-item div {
          flex: 1;
        }

        .step-item h4 {
          font-size: 14px;
          margin-bottom: 2px;
        }

        .step-item p {
          font-size: 12px;
          color: var(--text-muted);
        }

        .step-item svg {
          color: var(--text-muted);
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;
